import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import EnquiryForm from "@/components/EnquiryForm";
import {
    ArrowRight, GlobeHemisphereEast, IdentificationCard, GraduationCap, Airplane,
    ChatCircleDots, MapPin, Phone, EnvelopeSimple, CheckCircle, Buildings,
    Certificate, FileText, PaperPlaneTilt,
} from "@phosphor-icons/react";

const COUNTRIES = [
    { name: "USA",              flag: "🇺🇸", tagline: "Ivy Leagues · STEM OPT · F-1 visas", tone: "bg-[#1E3A8A]" },
    { name: "United Kingdom",   flag: "🇬🇧", tagline: "Russell Group · 2-year post-study visa", tone: "bg-[#7F1D1D]" },
    { name: "Canada",           flag: "🇨🇦", tagline: "PGWP · pathway to PR", tone: "bg-[#B91C1C]" },
    { name: "Australia",        flag: "🇦🇺", tagline: "Group of Eight · Subclass 500", tone: "bg-[#0C4A6E]" },
    { name: "New Zealand",      flag: "🇳🇿", tagline: "Post-study work visa · low cost of living", tone: "bg-[#065F46]" },
    { name: "UAE",              flag: "🇦🇪", tagline: "Global campuses · tax-free income", tone: "bg-[#7C2D12]" },
    { name: "European Union",   flag: "🇪🇺", tagline: "Germany · France · Ireland · Netherlands · Sweden · Italy · Spain", tone: "bg-[#365314]" },
];

const SERVICES = [
    { icon: GraduationCap, title: "University Shortlisting", body: "We map your profile, budget and goals to a shortlist of universities where you'll actually get in — no wishful thinking." },
    { icon: FileText,      title: "SOP & LOR Editing",       body: "Statement of Purpose, Letters of Recommendation, essays — reviewed, edited and re-drafted by experienced counsellors until they read like admits." },
    { icon: Certificate,   title: "Application Filing",       body: "Common App, UCAS, direct portals — every deadline tracked, every document checked. No missed dates." },
    { icon: IdentificationCard, title: "Visa Processing",     body: "End-to-end visa handling. Document checklist, financial-proof preparation, mock interviews, appointment booking, and follow-through until you get the stamp." },
    { icon: Airplane,      title: "Pre-Departure Briefing",   body: "Accommodation help, forex, SIM setup, banking, first-week survival guide — before you board the plane." },
    { icon: Buildings,     title: "On-Ground Partner Network",body: "Trusted housing, medical insurance and part-time-work partners in every destination country." },
];

const STEPS = [
    { n: "01", title: "Free consultation", body: "Walk into our Mehdipatnam office or call. We assess your profile, target country and budget in one conversation." },
    { n: "02", title: "Roadmap",            body: "You receive a written plan — tests to attempt, deadlines, universities, expected costs." },
    { n: "03", title: "Test prep",          body: "Wibpro Academy tracks (IELTS/PTE/GRE/GMAT/SAT) integrate seamlessly with your admissions timeline." },
    { n: "04", title: "Applications",       body: "We file everything, together. You focus on interviews and essays; we handle deadlines and portals." },
    { n: "05", title: "Visa",               body: "Document prep, financials, mock visa interview, appointment. We don't leave until you have your stamp." },
    { n: "06", title: "Fly & settle",       body: "Pre-departure briefing, on-ground contacts, first-month check-in." },
];

export default function StudyAbroadPage() {
    return (
        <div className="min-h-screen paper-grain" data-testid="study-abroad-page">
            <Navbar />

            {/* Hero */}
            <section className="mx-auto max-w-7xl px-6 pt-16 pb-20 grid lg:grid-cols-12 gap-12 items-end">
                <div className="lg:col-span-7 animate-fade-up">
                    <div className="flex items-center gap-2 text-xs tracking-[0.3em] uppercase text-ink/60 mb-5">
                        <span className="w-8 h-px bg-gold"></span>
                        <span>Overseas Education · Handled End-to-End</span>
                    </div>
                    <h1 className="font-serif font-black text-5xl md:text-7xl lg:text-[5.25rem] leading-[0.95] text-ink">
                        Study abroad,
                        <br />
                        <span className="italic font-light text-ink/80">the honest way.</span>
                    </h1>
                    <p className="mt-8 text-lg md:text-xl text-ink/70 max-w-xl leading-relaxed">
                        From your first university shortlist to your visa interview — Wibpro handles
                        the paperwork, the deadlines and the anxiety. You focus on getting in.
                    </p>
                    <div className="mt-10 flex flex-wrap items-center gap-4">
                        <a href="tel:+919100560075">
                            <Button size="lg" className="bg-ink text-bone hover:bg-ink/90 rounded-sm px-8 h-12 group" data-testid="btn-hero-call">
                                <Phone size={18} className="mr-2" weight="fill"/> Call +91 91005 60075
                            </Button>
                        </a>
                        <a href="#contact" className="text-sm underline underline-offset-4 decoration-gold decoration-2 text-ink hover:text-gold transition-colors" data-testid="btn-book-consult">
                            Book a free consultation →
                        </a>
                    </div>
                </div>
                <div className="lg:col-span-5 relative animate-fade-up" style={{ animationDelay: "0.15s" }}>
                    <div className="relative aspect-[4/5] overflow-hidden rounded-sm border-2 border-ink/10 shadow-2xl">
                        <img src="https://images.unsplash.com/photo-1523050854058-8df90110c9f1?crop=entropy&cs=srgb&fm=jpg&q=85" alt="Graduation" className="w-full h-full object-cover"/>
                        <div className="absolute inset-0 bg-gradient-to-t from-ink/70 to-transparent"></div>
                        <div className="absolute bottom-6 left-6 right-6 text-bone">
                            <div className="text-xs uppercase tracking-[0.3em] text-gold mb-2">Since 2010</div>
                            <div className="font-serif text-2xl leading-tight">Every admit begins<br/>with a well-mapped year.</div>
                        </div>
                    </div>
                </div>
            </section>

            {/* Countries */}
            <section className="border-y border-ink/10 bg-parchment/40">
                <div className="mx-auto max-w-7xl px-6 py-14">
                    <div className="flex items-baseline justify-between flex-wrap gap-3 mb-8">
                        <div>
                            <div className="text-xs uppercase tracking-[0.3em] text-gold mb-2">Destinations</div>
                            <h2 className="font-serif text-4xl text-ink">Seven regions. One counsellor.</h2>
                        </div>
                        <GlobeHemisphereEast size={32} weight="duotone" className="text-ink/70"/>
                    </div>
                    <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
                        {COUNTRIES.map(c => (
                            <div key={c.name} className={`p-5 rounded-sm text-bone ${c.tone}`} data-testid={`country-${c.name.toLowerCase().replace(/\s+/g,'-')}`}>
                                <div className="text-3xl mb-3">{c.flag}</div>
                                <div className="font-serif text-xl leading-tight">{c.name}</div>
                                <div className="text-xs text-bone/70 mt-1 leading-relaxed">{c.tagline}</div>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* Services */}
            <section className="mx-auto max-w-7xl px-6 py-20">
                <div className="grid md:grid-cols-12 gap-8 mb-12">
                    <div className="md:col-span-5">
                        <div className="text-xs uppercase tracking-[0.3em] text-gold mb-3">What we do</div>
                        <h2 className="font-serif text-4xl md:text-5xl text-ink leading-tight gold-underline inline">
                            End-to-end. <em className="font-light">Nothing outsourced.</em>
                        </h2>
                    </div>
                    <p className="md:col-span-7 text-ink/70 text-lg leading-relaxed md:mt-6">
                        You'll deal with the same team from your first walk-in to your visa stamp.
                        No handoffs, no black-box "third-party partners", no surprise fees.
                    </p>
                </div>

                <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
                    {SERVICES.map((s, i) => (
                        <div key={s.title} className="p-6 border border-ink/10 bg-bone hover:border-ink/30 hover:-translate-y-1 transition-[transform,border-color] duration-200 rounded-sm" data-testid={`service-${i}`}>
                            <s.icon size={28} weight="duotone" className="text-ink mb-5"/>
                            <div className="font-serif text-xl text-ink mb-2">{s.title}</div>
                            <div className="text-sm text-ink/70 leading-relaxed">{s.body}</div>
                        </div>
                    ))}
                </div>
            </section>

            {/* Process */}
            <section className="bg-ink text-bone py-20">
                <div className="mx-auto max-w-7xl px-6">
                    <div className="text-xs uppercase tracking-[0.3em] text-gold mb-3">The Wibpro process</div>
                    <h2 className="font-serif text-4xl md:text-5xl leading-tight max-w-2xl">Six steps between here and your admit letter.</h2>
                    <div className="mt-12 grid md:grid-cols-2 lg:grid-cols-3 gap-8">
                        {STEPS.map(s => (
                            <div key={s.n} className="border-l-2 border-gold/40 pl-5" data-testid={`step-${s.n}`}>
                                <div className="font-serif text-gold text-3xl leading-none mb-2">{s.n}</div>
                                <div className="font-serif text-xl mb-2">{s.title}</div>
                                <div className="text-sm text-bone/70 leading-relaxed">{s.body}</div>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* Visa spotlight */}
            <section className="mx-auto max-w-7xl px-6 py-20">
                <div className="grid md:grid-cols-12 gap-12 items-center">
                    <div className="md:col-span-5">
                        <div className="text-xs uppercase tracking-[0.3em] text-gold mb-3">Signature service</div>
                        <h2 className="font-serif text-4xl md:text-5xl text-ink leading-tight">Visa processing, done properly.</h2>
                    </div>
                    <div className="md:col-span-7">
                        <ul className="space-y-4 text-ink/80">
                            {[
                                "Document checklist tailored to your country and university",
                                "Financial proof structuring (loan / sponsor / affidavit)",
                                "SEVIS / CAS / COE assistance",
                                "Mock visa interviews with a former counsellor",
                                "Appointment booking and rescheduling",
                                "Post-approval briefing and airport-arrival guide",
                            ].map((line, i) => (
                                <li key={line} className="flex items-start gap-3 text-lg" data-testid={`visa-point-${i}`}>
                                    <CheckCircle size={20} weight="fill" className="text-gold mt-1 shrink-0"/>
                                    <span>{line}</span>
                                </li>
                            ))}
                        </ul>
                    </div>
                </div>
            </section>

            {/* Contact */}
            <section id="contact" className="border-t border-ink/10 bg-parchment/40">
                <div className="mx-auto max-w-7xl px-6 py-20 grid lg:grid-cols-2 gap-12">
                    <div>
                        <div className="text-xs uppercase tracking-[0.3em] text-gold mb-3">Come talk to us</div>
                        <h2 className="font-serif text-4xl md:text-5xl text-ink leading-tight">Free 30-minute consultation.</h2>
                        <p className="mt-4 text-ink/70">
                            Walk in, call, or WhatsApp. No commitment, no upselling — just an honest read of your options.
                        </p>
                        <div className="mt-8 space-y-4">
                            <div className="flex items-start gap-3" data-testid="contact-address">
                                <MapPin size={20} weight="fill" className="text-gold mt-1"/>
                                <div>
                                    <div className="font-serif text-lg text-ink">Wibpro Academy — Head Office</div>
                                    <div className="text-sm text-ink/70">2nd floor, Safa Arcade, Opposite pillar 15,<br/>Mehdipatnam, Hyderabad, Telangana, India</div>
                                </div>
                            </div>
                            <div className="flex items-start gap-3" data-testid="contact-phone">
                                <Phone size={20} weight="fill" className="text-gold mt-1"/>
                                <div>
                                    <div className="font-serif text-lg text-ink">
                                        <a href="tel:+919100560075" className="hover:text-gold">+91 91005 60075</a>
                                    </div>
                                    <div className="text-sm text-ink/60">Mon–Sat · 10 AM–7 PM IST</div>
                                </div>
                            </div>
                            <div className="flex items-start gap-3">
                                <EnvelopeSimple size={20} weight="fill" className="text-gold mt-1"/>
                                <div>
                                    <div className="font-serif text-lg text-ink">
                                        <a href="mailto:hello@wibpro.com" className="hover:text-gold">hello@wibpro.com</a>
                                    </div>
                                    <div className="text-sm text-ink/60">We reply within one working day.</div>
                                </div>
                            </div>
                        </div>
                    </div>
                    <div className="border border-ink/15 rounded-sm bg-bone p-8">
                        <div className="flex items-center gap-2 text-xs uppercase tracking-[0.3em] text-gold mb-3">
                            <ChatCircleDots size={14} weight="duotone"/> While you're here
                        </div>
                        <h3 className="font-serif text-3xl text-ink leading-tight">Sharpen your test scores while your applications go out.</h3>
                        <p className="mt-3 text-ink/70">
                            Every study-abroad journey starts with a test — IELTS, PTE, GRE, GMAT or SAT. Our AI-powered
                            practice tests and mock exams inside Wibpro Academy plug straight into your admissions plan.
                        </p>
                        <div className="mt-6 flex flex-wrap gap-2">
                            {["IELTS", "PTE", "GRE", "GMAT", "SAT"].map(t => (
                                <Link key={t} to={`/courses/${t.toLowerCase()}`} className="px-3 py-1.5 text-sm bg-ink text-bone rounded-sm hover:bg-ink/80" data-testid={`test-link-${t.toLowerCase()}`}>
                                    {t}
                                </Link>
                            ))}
                        </div>
                        <Link to="/register">
                            <Button className="mt-6 w-full bg-ink text-bone hover:bg-ink/90 rounded-sm h-11" data-testid="btn-enrol">
                                Enrol at Wibpro Academy <ArrowRight size={16} className="ml-2"/>
                            </Button>
                        </Link>
                    </div>
                </div>

                {/* Enquiry form */}
                <div className="mx-auto max-w-7xl px-6 pb-20">
                    <div className="grid lg:grid-cols-12 gap-10 items-start">
                        <div className="lg:col-span-5">
                            <div className="text-xs uppercase tracking-[0.3em] text-gold mb-3">Or drop a message</div>
                            <h3 className="font-serif text-3xl md:text-4xl text-ink leading-tight">
                                Not sure where to start?
                            </h3>
                            <p className="mt-4 text-ink/70">
                                Tell us your target country, degree and rough timeline. A senior counsellor
                                will call back — no obligation, no upsell.
                            </p>
                        </div>
                        <div className="lg:col-span-7">
                            <EnquiryForm
                                source="study-abroad"
                                interestPlaceholder="e.g. MS in USA · Sept 2026 intake"
                            />
                        </div>
                    </div>
                </div>
            </section>

            <Footer />
        </div>
    );
}
